import { isEmpty, omit, orderBy, uniq, uniqBy } from 'lodash';
import moment from 'moment';
import { call, put, SagaReturnType, select, takeEvery } from 'redux-saga/effects';
import {
  doFailedGetCumulativeOrderList,
  doSucceededCreateOrder,
  doSucceededGetCumulativeOrderList,
  doSucceededGetOrder,
  doSucceededGetOrderList,
  doSucceededUpdateOrder,
} from 'src/actions';
import { getClientApi, OrderApi, QuoteApi } from 'src/data-communication';
import {
  CancelOrderDto,
  CreateOrderDto,
  GetOrderDto,
  GetOrderListDto,
  GetOrderListQueryParamsDto,
  OrderDto,
  OrderStatusDto,
  UpdateOrderDto,
} from 'src/dtos';
import { FromTradingBlockServerEvent } from 'src/events';
import {
  findOrderCommission,
  findOrderPrice,
  findOrderTotalCost,
  mapGetOrdersListResponseDtoToModel,
  mapOrderDetailsDtoToModel,
} from 'src/mappers';
import { Order, OrderDetails, OrderEvent, OrderStatus } from 'src/models';
import { getPayloadFromErrorResponse, Maybe, Logger } from 'src/utils';

import { State, Type } from '../actions/utils';
import { TReduxAction } from '../typings/commonTypes';

import { safeSaga } from './utils';

const isOrderVisible = (order: Order, queryParams?: GetOrderListQueryParamsDto): boolean => {
  let result = true;

  let orderStatusList: string[] = [];

  if (queryParams?.orderStatusId) {
    orderStatusList = queryParams.orderStatusId.split(',');
    result = result && orderStatusList.includes(order.status.value.toString());
  }

  if (queryParams?.dateFrom) {
    const orderDate = moment(order.updatedAt).startOf('day');
    const fromDate = moment(queryParams.dateFrom, 'YYYY-MM-DD').startOf('day');
    result = result && orderDate.isSameOrAfter(fromDate);
  }

  if (queryParams?.dateTo) {
    const orderDate = moment(order.updatedAt).startOf('day');
    const toDate = moment(queryParams.dateTo, 'YYYY-MM-DD').startOf('day');
    result = result && orderDate.isSameOrBefore(toDate);
  }

  return result;
};

const findRemainingPages = ({
  pageSize,
  pageIndex,
  total,
}: {
  pageSize: number;
  pageIndex: number;
  total: number;
}): number[] => {
  const result: number[] = [];
  const pages = Math.ceil(total / pageSize);
  const nextPage = pageIndex + 1;

  for (let counter = nextPage; counter < pages; counter += 1) {
    result.push(counter);
  }

  return result;
};

const getRemainingOrderList = async ({
  accountId,
  authToken,
  pages,
  pageSize,
  queryParams,
}: {
  pages: number[];
  accountId: number;
  authToken: string;
  pageSize: number;
  queryParams: Omit<GetOrderListQueryParamsDto, 'pageIndex' | 'pageSize'>;
}): Promise<OrderDto[]> => {
  try {
    const responses = await Promise.all(
      pages.map(aPage =>
        getClientApi().order.list({
          params: { accountId },
          queryParams: {
            dateFrom: queryParams.dateFrom,
            dateTo: queryParams.dateTo,
            symbol: queryParams.symbol,
            orderStatusId: queryParams.orderStatusId,
            pageIndex: aPage,
            pageSize,
          },
          authToken,
        }),
      ),
    );

    return responses.reduce((all: OrderDto[], aResponse) => all.concat(aResponse.data), []);
  } catch (error) {
    Logger.error(`Error getting remaining order list:`, error);
    Logger.info('Fallback: use empty list');

    return [];
  }
};

const findAverageFillPriceFromUpdateEvent = (
  orderUpdateEvent: FromTradingBlockServerEvent.OrderUpdateEventPayload,
): number => {
  if (orderUpdateEvent.status.value !== OrderStatusDto.Filled) {
    return 0;
  }

  return orderUpdateEvent.price;
};

function* list(action: TReduxAction) {
  const { authToken } = yield select(state => state.auth.data);
  const dto: GetOrderListDto = action?.payload;
  let { data, total }: SagaReturnType<OrderApi['list']> = yield call(getClientApi().order.list, {
    params: dto.params,
    queryParams: dto.queryParams,
    authToken,
  });

  const symbols: string[] = data.reduce((all: string[], current) => {
    if (current.UnderlyingSymbol) {
      all.push(current.UnderlyingSymbol);
    }

    return all;
  }, []);
  let quotes: SagaReturnType<QuoteApi['list']> = { data: [] };

  try {
    if (!isEmpty(uniq(symbols))) {
      quotes = yield call(getClientApi().quote.list, { queryParams: { symbols: uniq(symbols).join(',') }, authToken });
    }
  } catch (error) {
    Logger.error(`Error getting quote media snap list:`, error);
    Logger.info('Fallback: use empty quote media snap list');
  }
  const orders: Order[] = mapGetOrdersListResponseDtoToModel(data, quotes.data);
  yield put(doSucceededGetOrderList({ data: orders, total }));
}

function* cumulativeList(action: TReduxAction) {
  try {
    const { authToken } = yield select(state => state.auth.data);
    const dto: GetOrderListDto = action?.payload;
    let { data, total }: SagaReturnType<OrderApi['list']> = yield call(getClientApi().order.list, {
      params: dto.params,
      queryParams: dto.queryParams,
      authToken,
    });

    const remainingPages = findRemainingPages({
      total,
      pageIndex: dto.queryParams?.pageIndex ?? 0,
      pageSize: dto.queryParams?.pageSize ?? 50,
    });
    const remainingOrders: SagaReturnType<typeof getRemainingOrderList> = yield call(getRemainingOrderList, {
      authToken,
      accountId: dto.params.accountId,
      pages: remainingPages,
      pageSize: dto.queryParams?.pageSize ?? 50,
      queryParams: omit(dto.queryParams, ['pageIndex', 'pageSize', 'fetchAll']),
    });
    data = data.concat(remainingOrders);

    const symbols: string[] = data.reduce((all: string[], current) => {
      if (current.UnderlyingSymbol) {
        all.push(current.UnderlyingSymbol);
      }

      return all;
    }, []);
    let quotes: SagaReturnType<QuoteApi['list']> = { data: [] };

    try {
      if (!isEmpty(uniq(symbols))) {
        quotes = yield call(getClientApi().quote.list, {
          queryParams: { symbols: uniq(symbols).join(',') },
          authToken,
        });
      }
    } catch (error) {
      Logger.error(`Error getting quote media snap list:`, error);
      Logger.info('Fallback: use empty quote media snap list');
    }
    const orders: Order[] = mapGetOrdersListResponseDtoToModel(data, quotes.data);
    yield put(doSucceededGetCumulativeOrderList({ data: orders, total }));
  } catch (error) {
    const payload = getPayloadFromErrorResponse(error);
    yield put(doFailedGetCumulativeOrderList(payload));
  }
}

function* retrieve(action: TReduxAction) {
  try {
    const { authToken } = yield select(state => state.auth.data);
    const dto: GetOrderDto = action?.payload;
    const order: SagaReturnType<OrderApi['retrieve']> = yield call(getClientApi().order.retrieve, {
      ...dto,
      authToken,
    });

    let quote: SagaReturnType<QuoteApi['retrieve']> | undefined;

    try {
      if (order.UnderlyingSymbol) {
        quote = yield call(getClientApi().quote.retrieve, { params: { symbol: order.UnderlyingSymbol }, authToken });
      }
    } catch (error) {
      Logger.error(`Error getting quote media snap list:`, error);
      Logger.info('Fallback: use empty quote snap list');
    }
    const newOrder: OrderDetails = mapOrderDetailsDtoToModel(order, quote);
    yield put(doSucceededGetOrder(newOrder));
  } catch (error) {
    const payload = getPayloadFromErrorResponse(error);
    yield put({
      type: State.actionFailed(Type.GET_ORDERS_ID),
      payload: { ...payload, id: action?.payload?.params?.id },
    });
  }
}

function* create(action: TReduxAction) {
  try {
    const { authToken } = yield select(state => state.auth.data);
    const dto: CreateOrderDto = action?.payload;
    const newOrder: SagaReturnType<OrderApi['create']> = yield call(getClientApi().order.create, {
      ...dto,
      authToken,
    });

    let quotes: SagaReturnType<QuoteApi['list']> = { data: [] };

    try {
      if (newOrder.UnderlyingSymbol) {
        quotes = yield call(getClientApi().quote.list, {
          queryParams: { symbols: newOrder.UnderlyingSymbol },
          authToken,
        });
      }
    } catch (error) {
      Logger.error(`Error getting quote media snap list for new order(${newOrder.UnderlyingSymbol}):`, error);
    }

    const newOrderModel = mapOrderDetailsDtoToModel(newOrder, quotes.data[0]);

    yield put(doSucceededCreateOrder(newOrderModel));

    const orderList: Order[] | undefined = yield select(state => state.orders.list.data?.data);
    const total: number | undefined = yield select(state => state.orders.list.data?.total);
    const orderListQueryParams: GetOrderListQueryParamsDto | undefined = yield select(
      state => state.orders.list.payload?.queryParams,
    );

    if (orderList && total !== undefined && isOrderVisible(newOrderModel, orderListQueryParams)) {
      yield put(doSucceededGetOrderList({ data: [newOrderModel, ...orderList], total: total + 1 }));
    }

    const cumulativeOrderList: Order[] | undefined = yield select(state => state.orders.cumulativeList.data?.data);
    const cumulativeTotal: number | undefined = yield select(state => state.orders.cumulativeList.data?.total);
    const cumulativeOrderListQueryParams: GetOrderListQueryParamsDto | undefined = yield select(
      state => state.orders.cumulativeList.payload?.queryParams,
    );

    if (
      cumulativeOrderList &&
      cumulativeTotal !== undefined &&
      isOrderVisible(newOrderModel, cumulativeOrderListQueryParams)
    ) {
      yield put(
        doSucceededGetCumulativeOrderList({
          data: [newOrderModel, ...cumulativeOrderList],
          total: cumulativeTotal + 1,
        }),
      );
    }
  } catch (error) {
    const payload = getPayloadFromErrorResponse(error);
    yield put({
      type: State.actionFailed(Type.CREATE_ORDER),
      payload,
    });
  }
}

function* cancel(action: TReduxAction) {
  try {
    const { authToken } = yield select(state => state.auth.data);
    const dto: CancelOrderDto = action?.payload;
    yield call(getClientApi().order.cancel, { ...dto, authToken });
    yield put({
      type: State.actionSucceeded(Type.CANCEL_ORDER),
    });
  } catch (error) {
    const payload = getPayloadFromErrorResponse(error);
    yield put({
      type: State.actionFailed(Type.CANCEL_ORDER),
      payload,
    });
  }
}

function* update(action: TReduxAction) {
  try {
    const { authToken } = yield select(state => state.auth.data);
    const dto: UpdateOrderDto = action?.payload;
    const updatedOrderDto: SagaReturnType<OrderApi['update']> = yield call(getClientApi().order.update, {
      ...dto,
      authToken,
    });
    const updatedOrder = mapOrderDetailsDtoToModel(updatedOrderDto);
    const orderList: Order[] | undefined = yield select(state => state.orders.list.data?.data);
    const total: number | undefined = yield select(state => state.orders.list.data?.total);
    const cumulativeOrderList: Order[] | undefined = yield select(state => state.orders.cumulativeList.data?.data);
    const cumulativeTotal: number | undefined = yield select(state => state.orders.cumulativeList.data?.total);
    const existingOrder =
      orderList?.find(order => order.id === updatedOrder.id) ??
      cumulativeOrderList?.find(order => order.id === updatedOrder.id);
    const updatedOrderModel = {
      ...updatedOrder,
      description: existingOrder?.description ?? updatedOrder.description,
    };

    yield put(doSucceededUpdateOrder(updatedOrderModel));

    if (orderList && total !== undefined) {
      yield put(
        doSucceededGetOrderList({
          data: orderList.map(order => (order.id === updatedOrderModel.id ? updatedOrderModel : order)),
          total,
        }),
      );
    }

    if (cumulativeOrderList && cumulativeTotal !== undefined) {
      yield put(
        doSucceededGetCumulativeOrderList({
          data: cumulativeOrderList.map(order => (order.id === updatedOrderModel.id ? updatedOrderModel : order)),
          total: cumulativeTotal,
        }),
      );
    }
  } catch (error) {
    const payload = getPayloadFromErrorResponse(error);
    yield put({
      type: State.actionFailed(Type.UPDATE_ORDER),
      payload,
    });
  }
}

function* updateListOnOrderUpdateEvent(action: TReduxAction) {
  try {
    const orderUpdateEvent: FromTradingBlockServerEvent.OrderUpdateEventPayload = action.payload;
    const orderList: Maybe<Order[]> = yield select(state => state.orders.list.data?.data);
    const orderListQueryParams: GetOrderListQueryParamsDto | undefined = yield select(
      state => state.orders.list.payload?.queryParams,
    );

    if (isEmpty(orderList)) {
      return;
    }

    const existingOrder = orderList?.find(order => order.id === orderUpdateEvent.id);

    if (existingOrder) {
      const status = new OrderStatus(orderUpdateEvent.status.value);
      const totalFillQuantity = existingOrder.filledQuantity + orderUpdateEvent.fillQuantity;
      const isPartial = totalFillQuantity < existingOrder.quantity && totalFillQuantity > 0;
      const price =
        findOrderPrice({
          type: existingOrder.type,
          status,
          averageFillPrice: findAverageFillPriceFromUpdateEvent(orderUpdateEvent),
          limit: existingOrder.limit,
          stop: existingOrder.stop,
          isPartial,
        }) ?? 0;
      const commission = findOrderCommission({ status, commissionAssessed: orderUpdateEvent.commission, isPartial });
      const totalCost = findOrderTotalCost({
        status,
        action: existingOrder.action,
        price,
        quantity: existingOrder.quantity,
        fillQuantity: totalFillQuantity,
        commission,
        isPartial,
      });
      const updatedExistingOrder: Order = {
        ...existingOrder,
        quantity: orderUpdateEvent.quantity,
        filledQuantity: totalFillQuantity,
        isPartial,
        price,
        commission,
        totalCost,
        status,
        updatedAt: orderUpdateEvent.createdAt,
      };

      const shouldBeAdded = isOrderVisible(updatedExistingOrder, orderListQueryParams);

      if (shouldBeAdded) {
        yield put(
          doSucceededGetOrderList({
            data: orderList?.map(anOrder => (anOrder.id === updatedExistingOrder.id ? updatedExistingOrder : anOrder)),
            total: orderList?.length ?? 0,
          }),
        );

        return;
      }

      yield put(
        doSucceededGetOrderList({
          data: orderList?.filter(order => order.id !== updatedExistingOrder.id) ?? [],
          total: (orderList?.length ?? 1) - 1,
        }),
      );

      return;
    }
  } catch (error: any) {
    Logger.error('Error updating order list on order update event.');
    Logger.error(error);
  }
}

function* updateCumulativeListOnOrderUpdateEvent(action: TReduxAction) {
  try {
    const orderUpdateEvent: FromTradingBlockServerEvent.OrderUpdateEventPayload = action.payload;
    const orderList: Maybe<Order[]> = yield select(state => state.orders.cumulativeList.data?.data);
    const orderListQueryParams: GetOrderListQueryParamsDto | undefined = yield select(
      state => state.orders.cumulativeList.payload?.queryParams,
    );

    if (isEmpty(orderList)) {
      return;
    }

    const existingOrder = orderList?.find(order => order.id === orderUpdateEvent.id);

    if (existingOrder) {
      const status = new OrderStatus(orderUpdateEvent.status.value);
      const totalFillQuantity = existingOrder.filledQuantity + orderUpdateEvent.fillQuantity;
      const isPartial = totalFillQuantity < existingOrder.quantity && totalFillQuantity > 0;
      const price =
        findOrderPrice({
          type: existingOrder.type,
          status,
          averageFillPrice: findAverageFillPriceFromUpdateEvent(orderUpdateEvent),
          limit: existingOrder.limit,
          stop: existingOrder.stop,
          isPartial,
        }) ?? 0;
      const commission = findOrderCommission({ status, commissionAssessed: orderUpdateEvent.commission, isPartial });
      const totalCost = findOrderTotalCost({
        status,
        action: existingOrder.action,
        price,
        quantity: existingOrder.quantity,
        fillQuantity: totalFillQuantity,
        commission,
        isPartial,
      });
      const updatedExistingOrder = {
        ...existingOrder,
        quantity: orderUpdateEvent.quantity,
        filledQuantity: totalFillQuantity,
        isPartial,
        price,
        commission,
        totalCost,
        status,
        updatedAt: orderUpdateEvent.createdAt,
      };

      const shouldBeAdded = isOrderVisible(updatedExistingOrder, orderListQueryParams);

      if (shouldBeAdded) {
        yield put(
          doSucceededGetCumulativeOrderList({
            data: orderList?.map(anOrder => (anOrder.id === updatedExistingOrder.id ? updatedExistingOrder : anOrder)),
            total: orderList?.length ?? 0,
          }),
        );

        return;
      }

      yield put(
        doSucceededGetCumulativeOrderList({
          data: orderList?.filter(order => order.id !== updatedExistingOrder.id) ?? [],
          total: (orderList?.length ?? 1) - 1,
        }),
      );

      return;
    }
  } catch (error: any) {
    Logger.error('Error updating cumulative order list on order update event.');
    Logger.error(error);
  }
}

function* updateDetailsOnOrderUpdateEvent(action: TReduxAction) {
  try {
    const orderUpdateEvent: FromTradingBlockServerEvent.OrderUpdateEventPayload = action.payload;

    if (orderUpdateEvent.status.value === OrderStatusDto.Acknowledged) {
      return;
    }

    const existingOrder: Maybe<OrderDetails> = yield select(state => state.orders.details[orderUpdateEvent.id]?.data);

    if (existingOrder) {
      // NOTE: fill quantity in the event is the total filled quantity (cumulative) for the order on staging, whereas in production is per event.
      // When there are no fills for an event then the fill quantity is zero ,e.g. order is cancelled or rejected.
      const newEvent: OrderEvent = {
        id: `${orderUpdateEvent.createdAt}|${orderUpdateEvent.status.value}`,
        fillPrice: findAverageFillPriceFromUpdateEvent(orderUpdateEvent),
        fillQuantity: orderUpdateEvent.fillQuantity,
        status: orderUpdateEvent.status,
        symbol: orderUpdateEvent.symbol,
        createdAt: orderUpdateEvent.createdAt,
      };
      const events = uniqBy([newEvent, ...(existingOrder.events ?? [])], 'id');

      if (events.length === (existingOrder.events?.length ?? 0)) {
        Logger.debug(
          `Skipped duplicate order update event ${orderUpdateEvent.id} ${orderUpdateEvent.status.value} ${orderUpdateEvent.createdAt}`,
        );

        return;
      }
      const status = new OrderStatus(orderUpdateEvent.status.value);
      const totalFillQuantity = existingOrder.filledQuantity + orderUpdateEvent.fillQuantity;
      const isPartial = totalFillQuantity < existingOrder.quantity && totalFillQuantity > 0;
      const price =
        findOrderPrice({
          type: existingOrder.type,
          status,
          averageFillPrice: findAverageFillPriceFromUpdateEvent(orderUpdateEvent),
          limit: existingOrder.limit,
          stop: existingOrder.stop,
          isPartial,
        }) ?? 0;
      const commission = findOrderCommission({ status, commissionAssessed: orderUpdateEvent.commission, isPartial });
      const totalCost = findOrderTotalCost({
        status,
        action: existingOrder.action,
        price,
        quantity: existingOrder.quantity,
        fillQuantity: totalFillQuantity,
        commission,
        isPartial,
      });
      const updatedOrder: OrderDetails = {
        ...existingOrder,
        quantity: orderUpdateEvent.quantity,
        filledQuantity: totalFillQuantity,
        isPartial,
        price,
        commission,
        totalCost,
        status,
        events: orderBy(events, [event => new Date(event.createdAt).getTime()], ['desc']),
      };
      yield put(doSucceededGetOrder(updatedOrder));

      return;
    }
  } catch (error) {
    Logger.error('Error updating order details on order update event.');
    Logger.error(error);
  }
}

/**
 * Orders sagas
 */
export const registerOrderSagas = function* () {
  yield takeEvery(State.actionRequested(Type.GET_ORDERS), safeSaga(list, Type.GET_ORDERS));
  yield takeEvery(State.actionRequested(Type.GET_CUMULATIVE_ORDER_LIST), cumulativeList);
  yield takeEvery(State.actionRequested(Type.GET_ORDERS_ID), retrieve);
  yield takeEvery(State.actionRequested(Type.CREATE_ORDER), create);
  yield takeEvery(State.actionRequested(Type.UPDATE_ORDER), update);
  yield takeEvery(State.actionRequested(Type.CANCEL_ORDER), cancel);
  yield takeEvery(Type.UPDATE_ORDER_FROM_EVENT, updateListOnOrderUpdateEvent);
  yield takeEvery(Type.UPDATE_ORDER_FROM_EVENT, updateDetailsOnOrderUpdateEvent);
  yield takeEvery(Type.UPDATE_ORDER_FROM_EVENT, updateCumulativeListOnOrderUpdateEvent);
};
